import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtNOK, fmtKwh, fmtNumber, fmtOre } from "@/lib/format";
import { PageHeader, Card, Skeleton } from "@/components/UIKit";
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis,
  Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar,
} from "recharts";
import {
  Users, Building2, Gauge, FileSignature, Zap, TrendingUp,
  Receipt, AlertCircle, MessageSquare, ArrowUpRight, ArrowDownRight, CheckCircle2, XCircle,
} from "lucide-react";

function KpiCard({ icon: Icon, label, value, sub, trend, testid, accent }) {
  return (
    <div className="bg-[#0c0c0c] border border-zinc-900 rounded-lg p-5 hover:border-zinc-800 transition-colors" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">{label}</div>
        <Icon className={`h-4 w-4 ${accent || "text-zinc-500"}`} />
      </div>
      <div className="font-heading text-3xl font-medium tracking-tight tabular-nums text-zinc-50">{value}</div>
      <div className="flex items-center gap-1.5 mt-1.5 text-xs">
        {trend && (
          trend > 0
            ? <span className="text-emerald-400 flex items-center gap-0.5"><ArrowUpRight className="h-3 w-3" /> {trend}%</span>
            : trend < 0
              ? <span className="text-red-400 flex items-center gap-0.5"><ArrowDownRight className="h-3 w-3" /> {trend}%</span>
              : null
        )}
        {sub && <span className="text-zinc-500">{sub}</span>}
      </div>
    </div>
  );
}

const chartAxis = { stroke: "#3f3f46", fontSize: 10, tickLine: false, axisLine: false };
const chartTooltip = {
  contentStyle: { background: "#0a0a0a", border: "1px solid #27272a", borderRadius: 8, fontSize: 12 },
  labelStyle: { color: "#a1a1aa" },
  itemStyle: { color: "#fafafa" },
};

export default function Dashboard() {
  const [kpi, setKpi] = useState(null);
  const [consumption, setConsumption] = useState([]);
  const [revenue, setRevenue] = useState([]);
  const [spot, setSpot] = useState([]);
  const [area, setArea] = useState("NO1");

  useEffect(() => {
    (async () => {
      const [k, c, r] = await Promise.all([
        api.get("/dashboard/kpi"),
        api.get("/dashboard/consumption-series?days=30"),
        api.get("/dashboard/revenue-series?days=30"),
      ]);
      setKpi(k.data); setConsumption(c.data); setRevenue(r.data);
    })();
  }, []);

  useEffect(() => {
    api.get(`/dashboard/spot-series?area=${area}&days=14`).then(({ data }) => setSpot(data));
  }, [area]);

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title="Dashboard"
        subtitle="Sanntidsoversikt over drift, kunder og økonomi"
      />

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3 mb-6">
        {kpi ? (
          <>
            <KpiCard testid="kpi-customers" icon={Users} label="Kunder totalt" value={fmtNumber(kpi.total_customers)} sub={`${kpi.b2c} privat · ${kpi.b2b} bedrift`} accent="text-emerald-400" />
            <KpiCard testid="kpi-contracts" icon={FileSignature} label="Aktive avtaler" value={fmtNumber(kpi.active_contracts)} sub="pågående" />
            <KpiCard testid="kpi-meters" icon={Gauge} label="Målepunkter" value={fmtNumber(kpi.total_meters)} sub="totalt i drift" />
            <KpiCard testid="kpi-consumption" icon={Zap} label="Forbruk 30d" value={fmtKwh(kpi.consumption_30d_kwh)} accent="text-amber-400" />
            <KpiCard testid="kpi-revenue" icon={TrendingUp} label="Omsetning 30d" value={fmtNOK(kpi.revenue_30d_nok)} sub={`Fortjeneste ${fmtNOK(kpi.profit_30d_nok)}`} accent="text-emerald-400" />
            <KpiCard testid="kpi-outstanding" icon={AlertCircle} label="Utestående" value={fmtNOK(kpi.outstanding_nok)} sub={`${kpi.open_cases} åpne saker`} accent="text-orange-400" />
          </>
        ) : (
          Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-[122px]" />)
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
        <Card testid="chart-revenue" className="xl:col-span-2 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Omsetning</div>
              <div className="font-heading text-xl text-zinc-50 mt-0.5">Siste 30 dager</div>
            </div>
            <div className="text-2xl font-heading font-medium tabular-nums">
              {kpi ? fmtNOK(kpi.revenue_30d_nok) : "—"}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={revenue}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#141414" vertical={false} />
              <XAxis dataKey="date" {...chartAxis} />
              <YAxis {...chartAxis} />
              <Tooltip {...chartTooltip} formatter={(v) => [fmtNOK(v), "NOK"]} />
              <Area type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={1.75} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card testid="chart-elhub" className="p-5">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium mb-3">Systemstatus</div>
          <div className="space-y-3 mt-2">
            <StatusRow ok={kpi?.elhub_configured} label="Elhub-integrasjon" note={kpi?.elhub_configured ? "Tilkoblet" : "Krever credentials"} />
            <StatusRow ok label="Nord Pool spotdata" note="Ekte day-ahead-priser, oppdateres hver time" />
            <StatusRow ok label="Prismotor" note="Aktiv (MVA · elavgift · strømstøtte)" />
            <StatusRow ok label="Fakturering (PDF)" note="Lokal generering (WeasyPrint)" />
            <StatusRow ok={false} label="EHF (PEPPOL)" note="Ikke konfigurert" />
            <StatusRow ok={false} label="Resend e-post" note="Ikke konfigurert" />
            <StatusRow ok={false} label="Twilio SMS" note="Ikke konfigurert" />
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card testid="chart-consumption" className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Forbruk</div>
              <div className="font-heading text-xl text-zinc-50 mt-0.5">Aggregert forbruk (30d)</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={consumption}>
              <CartesianGrid strokeDasharray="3 3" stroke="#141414" vertical={false} />
              <XAxis dataKey="date" {...chartAxis} />
              <YAxis {...chartAxis} />
              <Tooltip {...chartTooltip} formatter={(v) => [fmtKwh(v), ""]} />
              <Bar dataKey="kwh" fill="#3b82f6" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card testid="chart-spot" className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium">Nord Pool spotpris</div>
              <div className="font-heading text-xl text-zinc-50 mt-0.5">Snitt per døgn — {area}</div>
            </div>
            <div className="flex gap-1">
              {["NO1", "NO2", "NO3", "NO4", "NO5"].map((a) => (
                <button
                  key={a}
                  onClick={() => setArea(a)}
                  className={`px-2 py-1 rounded text-[11px] transition-colors ${area === a ? "bg-emerald-500/15 text-emerald-400" : "text-zinc-500 hover:text-zinc-200"}`}
                  data-testid={`area-${a}`}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={spot}>
              <CartesianGrid strokeDasharray="3 3" stroke="#141414" vertical={false} />
              <XAxis dataKey="date" {...chartAxis} />
              <YAxis {...chartAxis} />
              <Tooltip {...chartTooltip} formatter={(v) => [fmtOre(v), ""]} />
              <Line type="monotone" dataKey="ore" stroke="#f59e0b" strokeWidth={1.75} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}

function StatusRow({ ok, label, note }) {
  const Icon = ok ? CheckCircle2 : XCircle;
  const color = ok ? "text-emerald-400" : "text-zinc-600";
  return (
    <div className="flex items-center gap-3 text-sm">
      <Icon className={`h-4 w-4 ${color}`} />
      <div className="flex-1">
        <div className="text-zinc-200">{label}</div>
        <div className="text-[11px] text-zinc-500">{note}</div>
      </div>
    </div>
  );
}
